import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import { useHistory } from 'react-router-dom';
import { InfoBox, Searchbar } from "..";
import { AssetSvg } from "../../assets";
import { Pagination } from "../../components";
import { AppConstants, StepNumber, topLoaderProgress } from "../../configs";
import { useAppSelector, VRIAppStateType } from "../../core";
import { ColumnSelectionsModel, InvoiceModel, TableColumnExtend, } from "../../models";
import { ApiSchemaAuditLogsData } from "../../models/api/AuditLogs";
import { ApiSchemaCallCenterActivityData } from "../../models/api/CallCenterActivity";
import { ApiSchemaOperatorResponseData } from "../../models/api/Operator";
import { ApiSchemaFilter } from "../../models/api/filter";
import { CommonAuditActivityFilter } from "../Common/Components/CommonInAuditActivity/commonAuditActivityFilter";
import { NoDataComponent } from "./noDataComponent";
import { ConfirmationModal } from "../../features";
import { usePersistedPagingLocal } from "../../pages/hooks/usePersistedPagingLocal";

type Props = {
  pageTitle?: string;
  modalControlBtnLabel?: string;
  tableHeaders: TableColumnExtend<
    | InvoiceModel
    | ApiSchemaOperatorResponseData
    | ApiSchemaAuditLogsData
    | ApiSchemaCallCenterActivityData
    | any
  >[];
  tableData: any[];
  setCreateModalShow?: (value: boolean) => void;
  selectableRows?: boolean;
  handleSelectedRowsChange?: any;
  showFilter?: boolean;
  showExportOptions?: boolean;
  filterPanel?: string;
  setStepCounter?: any;
  pageSize?: number;
  pageIndex?: number;
  setPageIndex?: Function;
  count?: number;
  hasNextPage?: boolean;
  loading?: boolean;
  onChangePageIndex?: any;
  onChangePageSize?: any;
  searchText?: string;
  handleSearch?: any;
  enableAllToggle?: boolean;
  enableAllToggleHandler?: any;
  callLogInfoBoxData?: [string, string][];
  pageBreadCrumbTile?: string;
  hideSearchBar?: boolean;
  loaderProgress: number;
  onExportData?: () => Promise<Object>;
  isPaging?: boolean;
  pagedOperatorUrl?: string;
  hasPagingData?: boolean;
  setHasPagingData?: any;
  disablePaging?: boolean;
  pagingKey?: string;
  setFilterDataForAnyTable?: Function;
  setFilterDataChangeForAnyTable?: Function;
  onSortOverride?: (
    selectedColumn: TableColumnExtend<any>,
    sortDirection: "asc" | "desc"
  ) => boolean | void;
  showAutoRefresh?: boolean;
  columnSelectionModalShow?: boolean;
  setColumnSelectionModalShow?: (value: boolean) => void;
  columnSelection?: ColumnSelectionsModel[];
  setColumnSelection?: any;
  pageName?: "auditLogsPage" | "activityPage" | "statusPage" | "ongoingCallsPage";
  downloadedFileName?: string;
  tableType?: number;
  companyId?: string;
  callCenterId?: string;
  filterData?: ApiSchemaFilter;
  filterDataChange?: number;
  setLoaderProgress?: any;
  mutateUrl?: string;
  setIsShowCommonFilter?: any;
  isShowCommonFilter?: boolean;
  isHideAddButton?: boolean;
  isAccordionEnabled?: boolean;
  rowDetailsComponent?: React.ComponentType<{ data: any }>;
  noDataFoundText?: string;
  customFilterPanel?: React.ReactNode;
};
export function AdminPageTemplate(props: Props): JSX.Element {
  const {
    pageTitle,
    pageBreadCrumbTile,
    tableHeaders,
    tableData,
    setCreateModalShow,
    selectableRows,
    handleSelectedRowsChange,
    showFilter,
    showExportOptions,
    filterPanel,
    setStepCounter,
    modalControlBtnLabel,
    pageSize,
    pageIndex,
    setPageIndex,
    count,
    hasNextPage,
    loading,
    onChangePageIndex,
    onChangePageSize,
    searchText,
    handleSearch,
    enableAllToggle,
    enableAllToggleHandler,
    callLogInfoBoxData,
    hideSearchBar,
    loaderProgress,
    onExportData,
    isPaging,
    pagedOperatorUrl,
    hasPagingData,
    setHasPagingData,
    disablePaging,
    pagingKey,
    setFilterDataForAnyTable,
    setFilterDataChangeForAnyTable,
    onSortOverride,
    showAutoRefresh,
    columnSelectionModalShow,
    setColumnSelectionModalShow,
    columnSelection,
    setColumnSelection,
    pageName,
    downloadedFileName,
    tableType,
    companyId,
    callCenterId,
    filterData,
    filterDataChange,
    setLoaderProgress,
    mutateUrl,
    setIsShowCommonFilter,
    isShowCommonFilter,
    isHideAddButton = false,
    isAccordionEnabled = false,
    rowDetailsComponent,
    noDataFoundText,
    customFilterPanel
  } = props;
  const history = useHistory();
  const [noDataComponentText, setNoDataComponentText] = useState("Loading...");
  const [toggleModalShow, setToggleModalShow] = useState(false);

  const initialDefaultsRef = useRef({
    pageNo: pageIndex && pageIndex > 0 ? pageIndex : 1,
    pageSize: pageSize && pageSize > 0 ? pageSize : 10,
  });

  const storageKey = pagingKey ? `paging:${pagingKey}` : "";
  const { paging, setPaging } = usePersistedPagingLocal(
    storageKey,
    initialDefaultsRef.current
  );
  const { pageNo, pageSize: pageSizeLocal } = paging;

  useEffect(() => {
    onChangePageSize?.(pageSizeLocal);
    onChangePageIndex?.(pageNo);
  }, [pageNo, pageSizeLocal, onChangePageIndex, onChangePageSize]);

  const resetSignalRef = useRef({
    searchText,
    filterDataChange,
  });

  useEffect(() => {
    const prev = resetSignalRef.current;
    const searchChanged = (searchText ?? "") !== (prev.searchText ?? "");
    const filterChanged = filterDataChange !== prev.filterDataChange;

    if ((searchChanged || filterChanged) && pageNo !== 1) {
      setPaging((state) => ({ ...state, pageNo: 1 }));
    }

    resetSignalRef.current = { searchText, filterDataChange };
  }, [searchText, filterDataChange, pageNo, setPaging]);

  useEffect(() => {
  if (loaderProgress) {
    setNoDataComponentText("Loading...");
    if (loaderProgress === topLoaderProgress.complete) {
      setNoDataComponentText(
        tableData?.length === 0 ? (noDataFoundText ?? "no data found") : "Loading..."
      );
    }
  }

  if (!disablePaging) {
    setHasPagingData?.(tableData?.length !== 0);
  }
}, [loaderProgress, tableData, disablePaging, noDataFoundText, setHasPagingData]);

  useEffect(() => { }, [hasPagingData]);
  const data = useAppSelector((state: VRIAppStateType) => ({ userRole: state.auth.profile?.userRole }));

  const handleToggleModalShow = () => {
    setToggleModalShow(!toggleModalShow);
  };

  const handleToggleConfirmation = async () => {
    enableAllToggleHandler();
    setToggleModalShow(false);
  };

  const customStyles = {
    headCells: {
      style: {
        fontWeight: "700",
        fontSize: "13px",
        color: "#6e7476",
        textTransform: "uppercase",
      },
    },
  };

  return (
    <>
      {pageTitle ? (
        <h4 className="mt-3 mb-0 font-weight-light">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb bg-transparent p-0">
              {pageBreadCrumbTile ? (
                <li className="breadcrumb-item ">
                  <a
                    href="#"
                    onClick={() => history.goBack()}
                    className="font-weight-light"
                  >
                    {pageBreadCrumbTile}
                  </a>
                </li>
              ) : null}
              <li
                className="breadcrumb-item font-weight-light active"
                aria-current="page"
              >
                {" "}
                {pageTitle !== "Jobs" ? pageTitle : ""}{" "}
              </li>
            </ol>
          </nav>
        </h4>
      ) : null}

      <div className="row">
        <div className="col-12 d-flex mt-3">
          <div className="flex-shrink-0">
            {(modalControlBtnLabel && data.userRole === AppConstants.UserRoles.Admin) || ((data.userRole === AppConstants.UserRoles.ResellerAdmin || data.userRole === AppConstants.UserRoles.CompanyAdmin) && isHideAddButton) ? (
              <button
                className="btn btn-primary d-flex align-items-center mr-2"
                onClick={() => {
                  setCreateModalShow?.(true);
                  setStepCounter?.(StepNumber.firstStep);
                }}
              >
                <span className="flex-shrink-0 mr-2">
                  <span >
                    <AssetSvg.Plus />
                  </span>
                </span>
                <span>{modalControlBtnLabel}</span>
              </button>
            ) : null}
          </div>
          <div className="w-100">
            {!hideSearchBar ? (
              <Searchbar
                showFilter={showFilter}
                showExportOptions={showExportOptions}
                filterPanel={filterPanel}
                searchText={searchText}
                handleSearch={handleSearch}
                // columnSelectionModalShow={columnSelectionModalShow}
                // setColumnSelectionModalShow={setColumnSelectionModalShow}
                // mutateUrl={pagedOperatorUrl}
                showAutoRefresh={showAutoRefresh}
                columnSelectionModalShow={columnSelectionModalShow}
                setColumnSelectionModalShow={setColumnSelectionModalShow}
                columnSelection={columnSelection}
                setColumnSelection={setColumnSelection}
                tableType={tableType}
                downloadedFileName={downloadedFileName}
                companyId={companyId}
                callCenterId={callCenterId}
                filterData={filterData}
                setLoaderProgress={setLoaderProgress}
                mutateUrl={mutateUrl}
                setIsShowCommonFilter={setIsShowCommonFilter}
              />
            ) : null}
          </div>
        </div>
        {isShowCommonFilter &&
          (customFilterPanel ? (
            customFilterPanel
          ) : (
            <CommonAuditActivityFilter
              setFilterData={setFilterDataForAnyTable}
              setFilterDataChange={setFilterDataChangeForAnyTable}
              pageName={pageName!}
              setPageIndex={setPageIndex!}
            />
          ))}

        {enableAllToggle ? (
          <div className="col-12 mt-3">
            <div className="row justify-content-end flex-wrap align-items-center mx-0">
              <>
                <button
                  type="button"
                  className={"btn btn-toggle  active"}
                  onClick={() => setToggleModalShow(true)}
                >
                  <div className="handle"></div>
                </button>
                <span className="toggle-button-label  text-uppercase ">
                  Enable All
                </span>
              </>
            </div>
          </div>
        ) : pageTitle === "Call Outflow" ? (
          <div className="col-12 mt-3">
            <div className="row justify-content-end flex-wrap  align-items-center mx-0">
              <button
                type="button"
                className={"btn btn-toggle  disable"}
                onClick={() => setToggleModalShow(true)}
              >
                <div className="handle"></div>
              </button>
              <span className="toggle-button-label  text-uppercase ">
                Enable All
              </span>
            </div>
          </div>
        ) : null}

        {callLogInfoBoxData ? (
          <div className="col-12 mt-3">
            <div className="summary-box-wrapper d-flex ">
              <InfoBox data={callLogInfoBoxData} />
            </div>
          </div>
        ) : null}
        <div className="col-12 mt-3 mb-3">
          <div className="component-card ">
            <div className="component-card-header d-flex align-items-center justify-content-between border-top border-right border-left  ">
              <div>
                <span>{pageTitle}</span>
              </div>
            </div>
            <div className="component-card-body border-top border-bottom border-right border-left p-0">
              <DataTable
                columns={tableHeaders}
                noDataComponent={
                  <NoDataComponent title={noDataComponentText} />
                }
                data={tableData}
                selectableRows={selectableRows}
                onSelectedRowsChange={handleSelectedRowsChange}
                //@ts-ignore
                customStyles={customStyles}
                highlightOnHover={true}
                pointerOnHover={true}
                sortServer={true}
                expandableRows={isAccordionEnabled}
                expandableRowsComponent={rowDetailsComponent}
                //@ts-ignore
                onSort={(
                  selectedColumn: TableColumnExtend<
                    | InvoiceModel
                    | ApiSchemaOperatorResponseData
                    | ApiSchemaAuditLogsData
                    | ApiSchemaCallCenterActivityData
                  >,
                  sortDirection
                ) => {
                  const handled = onSortOverride?.(
                    selectedColumn,
                    sortDirection
                  );
                  if (handled) {
                    return;
                  }
                  setFilterDataForAnyTable &&
                    setFilterDataForAnyTable((prevState: ApiSchemaFilter) => {
                      return {
                        ...prevState,
                        sortQuery: {
                          columnName: selectedColumn.sortField,
                          direction: sortDirection === "asc" ? 1 : 2,
                        },
                      };
                    });
                  setFilterDataChangeForAnyTable &&
                    setFilterDataChangeForAnyTable(
                      Math.floor(Math.random() * 100) + 1
                    );
                }}
              />
              {hasPagingData && (
                <Pagination
                  pageSize={pageSizeLocal}
                  pageIndex={pageNo}
                  count={count}
                  hasNextPage={hasNextPage}
                  loading={loading}
                  onChangePageIndex={(next: number) => {
                    setPaging(p => ({ ...p, pageNo: next }));
                    onChangePageIndex?.(next);
                  }}
                    onChangePageSize={(next: number) => {
                      const Snew = Number(next);
                      let Pnew = pageNo; 

                      if (typeof count === 'number') {
                        const totalPages = Math.max(1, Math.ceil(count / Snew));
                        if (Pnew > totalPages) Pnew = totalPages;
                      }

                      setPaging({ pageNo: Pnew, pageSize: Snew });

                      onChangePageSize?.(Snew);
                      onChangePageIndex?.(Pnew);
                    }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        message={<>{enableAllToggle ? "disable" : "enable"} all</>}
        show={toggleModalShow}
        handleShow={handleToggleModalShow}
        handleConfirmation={handleToggleConfirmation}
      />
    </>
  );
}
